import {
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
    getDocument,
    GlobalWorkerOptions,
    PageViewport,
    PDFDocumentProxy,
    PDFPageProxy,
    RenderingCancelledException,
    RenderTask,
} from 'pdfjs-dist';
import { CodaltComponent } from '../../../codalt.component';
import { ImageViewerData } from './image-viewer-data';

/** Resolution the whole page is rendered at once, up front. High enough to look sharp unzoomed. */
const BASE_SCALE = 2.5;
/** Safe per-side canvas-size budget for every browser we support - iOS Safari in particular goes blank past roughly 4096x4096. */
const MAX_CANVAS_DIMENSION = 4096;
/** Safe total-area canvas budget, checked alongside {@link MAX_CANVAS_DIMENSION} to also cover oddly-shaped pages. */
const MAX_CANVAS_AREA = 4096 * 4096;

@Component({
    selector: 'app-image-modal',
    templateUrl: './image-modal.component.html',
    styleUrls: ['./image-modal.component.scss'],
    standalone: false,
})
export class ImageModalComponent extends CodaltComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('pdfCanvas') pdfCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('pdfDetailCanvas') pdfDetailCanvas!: ElementRef<HTMLCanvasElement>;
    @ViewChild('swiperEl') swiperEl!: ElementRef<HTMLElement>;
    @ViewChild('zoomContainerEl') zoomContainerEl!: ElementRef<HTMLElement>;
    @ViewChild('slideEl') slideEl!: ElementRef<HTMLElement>;

    @Input() data: ImageViewerData;
    @Input() showCloseButton = true;

    secure = '';

    images: { image: string }[] = [];
    currentPage = 1;
    numberOfPages: number[] = [];
    loadingPdf = false;
    fileNotFound = false;

    /** The currently open document, kept so page navigation can reuse it instead of re-downloading. */
    private pdf: PDFDocumentProxy | null = null;
    /** The currently displayed PDF.js page, source for both the base and the detail render. */
    private page: PDFPageProxy | null = null;

    /** In-flight render of the always-visible "whole page" canvas, if any - cancelled by a newer one. */
    private baseRenderTask: RenderTask | null = null;
    /** In-flight render of the "sharp crop of what's visible" overlay canvas, if any. */
    private detailRenderTask: RenderTask | null = null;

    /**
     * Off-screen canvas the detail crop is drawn into first, then copied onto the visible canvas in
     * one go once finished - painting straight into the visible canvas would flash it blank while
     * page.render() is still working, since render() clears its target canvas up front.
     */
    private readonly detailBuffer = document.createElement('canvas');

    /** Page width in PDF points (i.e. at PDF.js scale 1) - the unit the crop math converts on-screen pixels into. */
    private pageWidth = 0;
    /**
     * Bumped whenever an in-flight detail render should no longer be allowed to apply its result
     * (new page, or the user touched again) - the async render checks this before touching the DOM.
     */
    private detailGeneration = 0;
    /** requestAnimationFrame id of a pending, not-yet-run detail render; null when none is scheduled. */
    private detailFrame: number | null = null;

    // Swiper has no dedicated "zoom/pan gesture" events, so we piggyback on its generic touch
    // lifecycle: hide the (soon to be stale) sharp crop the moment a finger touches down, and only
    // render a fresh one once the finger lifts and the gesture is actually over.
    private readonly onInteractionStart = (): void => {
        this.hideDetail();
    };

    private readonly onInteractionEnd = (): void => {
        this.scheduleDetailRender();
    };

    // Releasing a pinch can trigger Swiper's momentum/snap-back CSS transition instead of settling
    // immediately - treat that the same way: hide while it's animating, re-render once it lands.
    private readonly onTransitionRun = (event: TransitionEvent): void => {
        if (this.isZoomTransition(event)) {
            this.hideDetail();
        }
    };

    private readonly onTransitionEnd = (event: TransitionEvent): void => {
        if (this.isZoomTransition(event)) {
            this.scheduleDetailRender();
        }
    };

    constructor(
        @Optional() public dialogRef: MatDialogRef<ImageModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) dialogData: ImageViewerData
    ) {
        super();
        if (dialogData) {
            this.data = dialogData;
        }
    }

    ngOnInit(): void {
        // Serve the PDF.js worker from Angular assets so it is copied and loaded as a static file.
        GlobalWorkerOptions.workerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

        if (this.data.secure) {
            this.secure = `secure=${this.data.secure}&`;
        }
        if (this.data.singlePdf) {
            void this.loadPdf(this.data.singlePdf);
        } else if (this.data.images?.length) {
            const viewIndex = Math.min(Math.max(this.data.viewIndex ?? 0, 0), this.data.images.length - 1);
            const images = this.data.images.map(image => ({ image }));

            this.images = [...images.slice(viewIndex), ...images.slice(0, viewIndex)];
        }
    }

    ngAfterViewInit(): void {
        if (!this.data.singlePdf) {
            return;
        }

        const swiper = this.swiperEl.nativeElement;
        const slide = this.slideEl.nativeElement;

        // 'swiper<name>' is how the swiper-container web component re-dispatches its internal
        // events as regular DOM events; transitionrun/transitionend are plain CSS events.
        swiper.addEventListener('swipertouchstart', this.onInteractionStart);
        swiper.addEventListener('swipertouchend', this.onInteractionEnd);
        slide.addEventListener('transitionrun', this.onTransitionRun);
        slide.addEventListener('transitionend', this.onTransitionEnd);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();
        const swiper = this.swiperEl?.nativeElement;
        const slide = this.slideEl?.nativeElement;
        swiper?.removeEventListener('swipertouchstart', this.onInteractionStart);
        swiper?.removeEventListener('swipertouchend', this.onInteractionEnd);
        slide?.removeEventListener('transitionrun', this.onTransitionRun);
        slide?.removeEventListener('transitionend', this.onTransitionEnd);
        if (this.detailFrame !== null) {
            cancelAnimationFrame(this.detailFrame);
        }
        this.baseRenderTask?.cancel();
        this.detailRenderTask?.cancel();
        void this.pdf?.destroy();
    }

    loadPage(page: number) {
        if (page === this.currentPage || this.loadingPdf || !this.pdf) {
            return;
        }

        this.currentPage = page;
        void this.renderCurrentPage();
    }

    private async loadPdf(url: string): Promise<void> {
        this.fileNotFound = false;
        this.loadingPdf = true;

        try {
            this.pdf = await getDocument({ url }).promise;

            this.numberOfPages = Array.from({ length: this.pdf.numPages }, (_, index) => index + 1);

            await this.renderCurrentPage();
        } catch {
            this.fileNotFound = true;
            this.numberOfPages = [];
        } finally {
            this.loadingPdf = false;
        }
    }

    private async renderCurrentPage(): Promise<void> {
        if (!this.pdf) {
            return;
        }

        this.loadingPdf = true;
        this.hideDetail();

        try {
            this.page = await this.pdf.getPage(this.currentPage);

            const viewport = this.page.getViewport({ scale: 1 });
            this.pageWidth = viewport.width;

            const desiredWidth = viewport.width * BASE_SCALE;
            const desiredHeight = viewport.height * BASE_SCALE;
            const fitted = this.fitCanvas(desiredWidth, desiredHeight);

            await this.renderBase(this.page, BASE_SCALE * fitted.factor, fitted.width, fitted.height);

            this.scheduleDetailRender();
        } finally {
            this.loadingPdf = false;
        }
    }

    /**
     * cancel() rejects the task's promise asynchronously; starting a new render on the same
     * canvas/context before that settles can leave the canvas blank on some browsers.
     * @param task the render to cancel, if one is currently in flight.
     */
    private async cancelAndAwait(task: RenderTask | null): Promise<void> {
        task?.cancel();
        if (task) {
            await task.promise.catch(() => {});
        }
    }

    private async renderBase(page: PDFPageProxy, scale: number, width: number, height: number): Promise<void> {
        await this.cancelAndAwait(this.baseRenderTask);

        const canvas = this.pdfCanvas.nativeElement;
        const context = canvas.getContext('2d')!;
        const viewport = page.getViewport({ scale });

        canvas.width = width;
        canvas.height = height;

        const task = page.render({
            canvasContext: context,
            viewport,
        });

        this.baseRenderTask = task;

        try {
            await task.promise;
        } catch (error) {
            if (!(error instanceof RenderingCancelledException)) {
                throw error;
            }
        } finally {
            if (this.baseRenderTask === task) {
                this.baseRenderTask = null;
            }
        }
    }

    private scheduleDetailRender(): void {
        if (this.detailFrame !== null) {
            cancelAnimationFrame(this.detailFrame);
        }

        this.detailFrame = requestAnimationFrame(() => {
            this.detailFrame = null;
            void this.renderVisibleDetail();
        });
    }

    /**
     * Renders a sharp crop of just the part of the page that's actually on screen right now, at
     * full device pixel density - instead of ever re-rendering the whole (possibly huge) page at a
     * higher resolution, which is both slow and the thing that runs into iOS Safari's canvas limit.
     */
    private async renderVisibleDetail(): Promise<void> {
        if (!this.page || this.loadingPdf || !this.pageWidth) {
            return;
        }

        const baseCanvas = this.pdfCanvas.nativeElement;
        const detailCanvas = this.pdfDetailCanvas.nativeElement;

        // clientWidth is the canvas's own un-transformed CSS size (Swiper zooms it via a CSS
        // transform, which doesn't affect clientWidth) - our anchor for the crop math below.
        const layoutWidth = baseCanvas.clientWidth;

        if (!layoutWidth) {
            return;
        }

        // getBoundingClientRect() already reflects Swiper's live pan/zoom transform, so we don't
        // need to reconstruct that transform (translate + scale + origin) by hand.
        const imageRect = baseCanvas.getBoundingClientRect();
        const viewportRect = this.slideEl.nativeElement.getBoundingClientRect();

        // Intersect the (possibly zoomed-and-panned-off-screen) page with the visible slide area.
        const visibleLeft = Math.max(imageRect.left, viewportRect.left);
        const visibleTop = Math.max(imageRect.top, viewportRect.top);
        const visibleRight = Math.min(imageRect.right, viewportRect.right);
        const visibleBottom = Math.min(imageRect.bottom, viewportRect.bottom);

        const dpr = window.devicePixelRatio || 1;
        // If the base canvas already has at least `dpr` backing pixels for every CSS pixel it's
        // currently displayed at, it's already as sharp as the screen can show - no crop needed.
        const basePixelsPerCssPixel = baseCanvas.width / imageRect.width;

        if (visibleRight <= visibleLeft || visibleBottom <= visibleTop || basePixelsPerCssPixel >= dpr) {
            detailCanvas.style.display = 'none';
            return;
        }

        // How much CSS-pixel space the pinch zoom currently occupies vs. the un-zoomed layout, and
        // how many CSS pixels one PDF point maps to - together they translate on-screen coordinates
        // back into PDF point-space for the getViewport() offset below.
        const zoom = imageRect.width / layoutWidth;
        const cssPixelsPerPdfPoint = layoutWidth / this.pageWidth;
        const pdfX = (visibleLeft - imageRect.left) / zoom / cssPixelsPerPdfPoint;
        const pdfY = (visibleTop - imageRect.top) / zoom / cssPixelsPerPdfPoint;

        const visibleWidth = visibleRight - visibleLeft;
        const visibleHeight = visibleBottom - visibleTop;
        const desiredWidth = visibleWidth * dpr;
        const desiredHeight = visibleHeight * dpr;
        const fitted = this.fitCanvas(desiredWidth, desiredHeight);
        const renderScale = cssPixelsPerPdfPoint * zoom * dpr * fitted.factor;

        // offsetX/offsetY shift PDF.js's render output so only the visible crop lands inside our
        // (much smaller) canvas, instead of rendering the full page at this resolution.
        const viewport = this.page.getViewport({
            scale: renderScale,
            offsetX: -(pdfX * renderScale),
            offsetY: -(pdfY * renderScale),
        });

        await this.renderDetail(this.page, viewport, fitted.width, fitted.height, {
            left: visibleLeft - viewportRect.left,
            top: visibleTop - viewportRect.top,
            width: visibleWidth,
            height: visibleHeight,
        });
    }

    /**
     * @param position where and how big to display the result, in CSS pixels relative to the slide.
     */
    private async renderDetail(
        page: PDFPageProxy,
        viewport: PageViewport,
        width: number,
        height: number,
        position: { left: number; top: number; width: number; height: number }
    ): Promise<void> {
        const generation = ++this.detailGeneration;

        await this.cancelAndAwait(this.detailRenderTask);

        if (generation !== this.detailGeneration) {
            return;
        }

        const buffer = this.detailBuffer;

        buffer.width = width;
        buffer.height = height;

        const task = page.render({
            canvasContext: buffer.getContext('2d')!,
            viewport,
        });

        this.detailRenderTask = task;

        try {
            await task.promise;
        } catch {
            // Detail rendering is only an enhancement - on any failure (including a cancellation,
            // which just means a newer render superseded this one), keep the base canvas visible.
            return;
        } finally {
            if (this.detailRenderTask === task) {
                this.detailRenderTask = null;
            }
        }

        if (generation !== this.detailGeneration) {
            return;
        }

        const visible = this.pdfDetailCanvas.nativeElement;

        visible.width = width;
        visible.height = height;
        visible.getContext('2d')!.drawImage(buffer, 0, 0);

        visible.style.left = `${position.left}px`;
        visible.style.top = `${position.top}px`;
        visible.style.width = `${position.width}px`;
        visible.style.height = `${position.height}px`;
        visible.style.display = 'block';
    }

    private hideDetail(): void {
        if (this.detailFrame !== null) {
            cancelAnimationFrame(this.detailFrame);
            this.detailFrame = null;
        }

        ++this.detailGeneration;

        this.detailRenderTask?.cancel();

        this.pdfDetailCanvas.nativeElement.style.display = 'none';
    }

    /**
     * @param width desired canvas width, before any shrinking to fit the safe pixel budget.
     * @param height desired canvas height, before any shrinking to fit the safe pixel budget.
     * @returns `width`/`height` shrunk (preserving aspect ratio) to fit the safe pixel budget, and
     * the `factor` they were shrunk by (1 if they already fit) - the caller multiplies its render
     * scale by this same factor so the rendered content matches the (possibly smaller) canvas.
     */
    private fitCanvas(
        width: number,
        height: number
    ): {
        width: number;
        height: number;
        factor: number;
    } {
        const factor = Math.min(
            1,
            MAX_CANVAS_DIMENSION / width,
            MAX_CANVAS_DIMENSION / height,
            Math.sqrt(MAX_CANVAS_AREA / (width * height))
        );

        return {
            width: Math.round(width * factor),
            height: Math.round(height * factor),
            factor,
        };
    }

    private isZoomTransition(event: TransitionEvent): boolean {
        return (
            event.propertyName === 'transform' &&
            (event.target === this.pdfCanvas.nativeElement || event.target === this.zoomContainerEl.nativeElement)
        );
    }

    @HostListener('click', ['$event'])
    public click(event) {
        if (event?.target?.localName !== 'img' && event?.srcElement?.className.indexOf('wiper-zoom-container') !== -1) {
            this.dialogRef?.close();
        }
    }
}
